import { toUtcDateKey } from '@/utils/date';
import { omit, stableStringify } from '@/utils/object';
import { type DowntimeRuleInput, isPerpetual, type MarketMakerInput } from '../mm-program-schema';

/** A rule as the Downtime view edits it: the markets it covers carry their own ids. */
export type DowntimeRuleTerms = WithoutId<DowntimeRuleInput>;
type WithoutId<Rule> = Rule extends unknown ? Omit<Rule, 'id'> : never;

/**
 * One rule across markets. Settings store downtime per instrument with an id unique in the document, so a rule set on
 * several markets is stored as one copy per market; copies with the same terms are shown and edited as one rule.
 */
export interface DowntimeGroup {
  /** The terms, serialised; unique within a market maker */
  key: string;
  terms: DowntimeRuleTerms;
  /** Sorted */
  instruments: string[];
  /** The stored copies' ids by instrument; more than one when a market has the same rule twice */
  ids: Record<string, string[]>;
}

const termsOf = (rule: DowntimeRuleInput): DowntimeRuleTerms => omit(rule, 'id');

export const termsKey = (terms: DowntimeRuleTerms) => stableStringify(terms);

/** The markets downtime can be set on: the market maker's perpetual programs, as the market maker editor offers them. */
export const downtimeMarketsOf = (document: MarketMakerInput) =>
  Object.keys(document.programs ?? {})
    .filter(isPerpetual)
    .sort();

/** The document's downtime rules, with copies of the same rule on several markets merged, in stored order. */
export const groupDowntime = (document: MarketMakerInput): DowntimeGroup[] => {
  const groups = new Map<string, DowntimeGroup>();
  for (const instrument of Object.keys(document.downtime ?? {}).sort()) {
    for (const rule of document.downtime?.[instrument] ?? []) {
      const terms = termsOf(rule);
      const key = termsKey(terms);
      const group = groups.get(key) ?? { key, terms, instruments: [], ids: {} };
      if (!group.ids[instrument]) group.instruments.push(instrument);
      group.ids[instrument] = [...(group.ids[instrument] ?? []), rule.id];
      groups.set(key, group);
    }
  }
  return [...groups.values()];
};

/** Drops instrument lists left empty, and the downtime field once nothing is left, so removing every rule leaves no trace. */
const withDowntime = (document: MarketMakerInput, downtime: Record<string, DowntimeRuleInput[]>): MarketMakerInput => {
  const kept = Object.fromEntries(Object.entries(downtime).filter(([, rules]) => rules.length));
  return Object.keys(kept).length ? { ...document, downtime: kept } : (omit(document, 'downtime') as MarketMakerInput);
};

/**
 * Sets a rule on `instruments`, replacing `previous` when editing one. A market that already had the rule keeps its
 * first copy's id and position, so an edit shows as a change to that copy; a newly picked market gets a new copy at the end.
 */
export const putDowntimeGroup = (
  document: MarketMakerInput,
  previous: DowntimeGroup | null,
  terms: DowntimeRuleTerms,
  instruments: string[],
  newId: () => string = () => crypto.randomUUID(),
): MarketMakerInput => {
  const picked = new Set(instruments);
  const downtime: Record<string, DowntimeRuleInput[]> = {};
  for (const [instrument, rules] of Object.entries(document.downtime ?? {})) {
    const ids = previous?.ids[instrument] ?? [];
    downtime[instrument] = rules.flatMap((rule) => {
      if (!ids.includes(rule.id)) return [rule];
      return picked.has(instrument) && rule.id === ids[0] ? [{ ...terms, id: rule.id } as DowntimeRuleInput] : [];
    });
  }
  for (const instrument of [...picked].sort()) {
    if (previous?.ids[instrument]) continue;
    downtime[instrument] = [...(downtime[instrument] ?? []), { ...terms, id: newId() } as DowntimeRuleInput];
  }
  return withDowntime(document, downtime);
};

/** Removes every copy of the rule. */
export const removeDowntimeGroup = (document: MarketMakerInput, group: DowntimeGroup): MarketMakerInput =>
  withDowntime(
    document,
    Object.fromEntries(Object.entries(document.downtime ?? {}).map(([instrument, rules]) => [instrument, rules.filter((rule) => !group.ids[instrument]?.includes(rule.id))])),
  );

/** Whether the rule covers no time from now on: a one-off that is over, or a recurring rule whose last day has passed. */
export const hasDowntimeEnded = (terms: DowntimeRuleTerms, now = new Date()) =>
  terms.type === 'one_off' ? Date.parse(terms.end) <= now.getTime() : terms.end_date !== undefined && terms.end_date <= toUtcDateKey(now);
